'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './MetricCard.module.css';

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  color: 'blue' | 'pink' | 'orange';
  title: string;
  value: string | number;
  linkText?: string;
  linkHref?: string;
  onLinkClick?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  (
    {
      color,
      title,
      value,
      linkText,
      linkHref,
      onLinkClick,
      icon,
      className,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    const [showIcon, setShowIcon] = React.useState(true);

    React.useEffect(() => {
      const el = innerRef.current;
      if (!el || !icon) return;
      const observer = new ResizeObserver(([entry]) => {
        setShowIcon(entry.contentRect.width >= 200);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [icon]);

    return (
      <div
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={clsx(styles.card, styles[color], className)}
        {...props}
      >
        <div className={styles.content}>
          <h3 className={styles.title}>{title}</h3>
          <span className={styles.value}>{value}</span>
          {linkText && (
            <a
              href={linkHref || '#'}
              className={styles.link}
              onClick={onLinkClick}
            >
              {linkText}
            </a>
          )}
        </div>
        {icon && showIcon && <div className={styles.icon}>{icon}</div>}
      </div>
    );
  }
);

MetricCard.displayName = 'MetricCard';

export default MetricCard;
